// A synthetic AFL history, so a backtest can run on a phone that has never
// signed in.
//
// The server holds the real seasons and will not hand them to an anonymous
// caller, which leaves two honest options: show a signed-out punter a locked
// door, or run the thing on data that is openly made up. This is the second.
// Everything here is INVENTED — the results, the prices and the teams' form —
// and every screen that runs on it says so.
//
// It is a fixture, not a fake feed, so it is pure: one seeded mulberry32 per
// season drives every draw, nothing reads the clock, and two calls produce
// deeply equal output. That matters more than it sounds — a demo backtest that
// printed a different ROI each time would teach the one lesson this app is
// trying to unteach, that a backtest is a prediction.
//
// The shape is `MatchLine[]`, the same rows `server/history/store.mjs` writes
// and `engine/universe.js` reduces, so the demo and the real thing run through
// identical code.

import aflTeams from './engine/teams/afl.json' with { type: 'json' };
import { matchKey as makeMatchKey } from './engine/teams.js';

/** Sports the demo carries. One is enough to show what a backtest looks like. */
export const DEMO_SPORTS = ['afl'];

/** Two seasons: the first is warm-up for a model, the second is the test. */
export const DEMO_SEASONS = ['2024', '2025'];

/** The soft book a demo backtest bets at, and the sharp one it is priced against. */
export const DEMO_BOOKMAKER = 'sportsbet';
export const DEMO_SHARP = 'betfair_ex_au';

const ROUNDS = 18;
const MATCHES_PER_ROUND = 9;

/**
 * The Saturday each season's round one falls on, in UTC. Both are after the
 * first Sunday in April, so every round is AEST (UTC+10) and 04:00Z is 14:00
 * in Melbourne without a timezone table in the fixture.
 */
const SEASON_START = { 2024: '2024-04-13', 2025: '2025-04-12' };

/** Home-ground advantage, in the same rating points elo.js trades in. */
const HGA = 35;

/** How far the two books' opinions wander from the truth. */
const SHARP_NOISE = 0.03;
const SOFT_NOISE = 0.15;

/** What each book charges. Sportsbet's margin is the reason most days print nothing. */
const SHARP_OVERROUND = 1.02;
const SOFT_OVERROUND = 1.05;

/** How far a closing price may drift toward the side that went on to win. */
const CLOSING_DRIFT = 0.04;

const DAY_MS = 86_400_000;

/** Deterministic 32-bit PRNG. Same seed, same sequence, forever. */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable 32-bit number for a string — the seed for a team's hidden strength. */
function hash32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * The hidden strength table — the thing a model is trying to find and no
 * screen ever shows. Rating points, roughly the spread of a real ladder.
 */
function strengths(sport, teams, season) {
  const drift = mulberry32(hash32(`${sport}:${season}:drift`));
  const out = {};
  for (const team of teams) {
    const base = ((hash32(`${sport}:${team.id}`) % 1201) - 600) / 10;   // ±60
    out[team.id] = base + (drift() - 0.5) * 30;                        // ±15 a season
  }
  return out;
}

/** The elo curve, so a demo season is a season the elo model can actually learn. */
const winProb = (home, away) => 1 / (1 + Math.pow(10, -((home + HGA - away) / 400)));

/**
 * A full round-robin by the circle method: 17 rounds of 9, every club playing
 * every other once. Round 18 replays round one with the ground swapped.
 */
function fixtures(ids) {
  const n = ids.length;
  const anchor = ids[0];
  let rest = ids.slice(1);
  const rounds = [];
  for (let r = 0; r < n - 1; r++) {
    const round = [[anchor, rest[0]]];
    for (let i = 1; i < n / 2; i++) round.push([rest[i], rest[rest.length - i]]);
    rounds.push(round);
    rest = [rest[rest.length - 1], ...rest.slice(0, -1)];
  }
  rounds.push(rounds[0].map(([home, away]) => [away, home]));
  return rounds.slice(0, ROUNDS);
}

function priceRow(bookmaker, selection, price, kind, capturedAt) {
  return {
    bookmaker,
    market: 'h2h',
    selection,
    price: round2(price),
    lay: null,
    liquidity: null,
    kind,
    capturedAt,
    source: 'demo',
  };
}

/**
 * One book's pair of prices for a match: its own noisy view of the home side's
 * chance, plus its margin. Rounded to the cent, the way a board prints.
 */
function board(pHome, noise, overround, rand) {
  const p = clamp(pHome + (rand() - 0.5) * 2 * noise, 0.05, 0.95);
  return { home: 1 / (p * overround), away: 1 / ((1 - p) * overround) };
}

/** Opening nudged toward whoever went on to win — the close, as it usually moves. */
function close(opening, winner, rand) {
  const drift = rand() * CLOSING_DRIFT;
  return {
    home: winner === 'home' ? opening.home * (1 - drift) : opening.home * (1 + drift),
    away: winner === 'away' ? opening.away * (1 - drift) : opening.away * (1 + drift),
  };
}

/** A margin consistent with the winner: never zero, wider when the gap was wider. */
function scores(winner, pHome, rand) {
  const expected = 12 + Math.abs(pHome - 0.5) * 90;
  const margin = Math.max(1, Math.round(expected + (rand() - 0.5) * 50));
  const base = 70 + Math.round(rand() * 30);
  return winner === 'home'
    ? { homeScore: base + margin, awayScore: base }
    : { homeScore: base, awayScore: base + margin };
}

/**
 * One season of made-up AFL: 18 rounds of 9, Saturdays from 14:00 Melbourne,
 * half-hourly. Every match is settled and carries opening and closing prices
 * at both books.
 */
export function demoSeason(sport = 'afl', season = '2025') {
  const teams = aflTeams;
  const ids = teams.map((t) => t.id);
  const nameOf = Object.fromEntries(teams.map((t) => [t.id, t.name]));
  const strength = strengths(sport, teams, season);
  const rand = mulberry32(hash32(`${sport}:${season}:season`));
  const rounds = fixtures(ids);
  const start = Date.parse(`${SEASON_START[season] ?? SEASON_START[2025]}T04:00:00.000Z`);

  const matches = [];
  rounds.forEach((round, roundIdx) => {
    round.slice(0, MATCHES_PER_ROUND).forEach(([homeId, awayId], matchIdx) => {
      const kickoffMs = start + roundIdx * 7 * DAY_MS + matchIdx * 30 * 60_000;
      const kickoff = new Date(kickoffMs).toISOString();
      const opened = new Date(kickoffMs - 7 * DAY_MS).toISOString();

      const pHome = winProb(strength[homeId], strength[awayId]);
      const winner = rand() < pHome ? 'home' : 'away';

      const softOpen = board(pHome, SOFT_NOISE, SOFT_OVERROUND, rand);
      const sharpOpen = board(pHome, SHARP_NOISE, SHARP_OVERROUND, rand);
      const softClose = close(softOpen, winner, rand);
      const sharpClose = close(sharpOpen, winner, rand);

      matches.push({
        matchKey: makeMatchKey(sport, kickoff, homeId, awayId),
        sport,
        competition: 'AFL (demo)',
        season,
        round: String(roundIdx + 1),
        kickoff,
        venue: null,
        home: { id: homeId, name: nameOf[homeId] },
        away: { id: awayId, name: nameOf[awayId] },
        result: { ...scores(winner, pHome, rand), winner, status: 'final' },
        prices: [
          priceRow(DEMO_BOOKMAKER, 'home', softOpen.home, 'opening', opened),
          priceRow(DEMO_BOOKMAKER, 'away', softOpen.away, 'opening', opened),
          priceRow(DEMO_SHARP, 'home', sharpOpen.home, 'opening', opened),
          priceRow(DEMO_SHARP, 'away', sharpOpen.away, 'opening', opened),
          priceRow(DEMO_BOOKMAKER, 'home', softClose.home, 'closing', kickoff),
          priceRow(DEMO_BOOKMAKER, 'away', softClose.away, 'closing', kickoff),
          priceRow(DEMO_SHARP, 'home', sharpClose.home, 'closing', kickoff),
          priceRow(DEMO_SHARP, 'away', sharpClose.away, 'closing', kickoff),
        ],
        sources: ['demo'],
      });
    });
  });
  return matches;
}

/** Every demo season for a sport, kickoff order. */
export function demoHistory(sport = 'afl') {
  if (!DEMO_SPORTS.includes(sport)) return [];
  return DEMO_SEASONS.flatMap((season) => demoSeason(sport, season));
}

/** `loader(sport, season)` over the demo history, for `runBacktest`. */
export function demoLoader(sport, season) {
  return demoHistory(sport).filter((m) => m.season === season);
}

/** The demo history in the shape `GET /api/history/index` answers with. */
export function demoIndex() {
  const sports = {};
  for (const sport of DEMO_SPORTS) {
    const all = demoHistory(sport);
    sports[sport] = {
      seasons: DEMO_SEASONS.map((season) => {
        const rows = all.filter((m) => m.season === season);
        const kickoffs = rows.map((m) => m.kickoff).sort();
        return {
          season,
          matches: rows.length,
          from: kickoffs[0] ?? null,
          to: kickoffs[kickoffs.length - 1] ?? null,
          hasOpening: true,
          hasClosing: true,
        };
      }),
    };
  }
  // `synthetic` is the flag every screen reads to say the numbers are invented.
  return { sports, updatedAt: null, synthetic: true };
}
